/**
 * P-14 (preferences, CP-3) + P-18 (consent) settings.
 *
 * Per-category notification channel toggles (push / sms / inbox) and a consent panel: the
 * required agreements (accepted, or to accept at the current version), and the optional ones the
 * patient can switch on and off — pharmacy sharing and transactional messages (ACCT-04, D-10).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { describeError } from "../../api/errors";
import {
  grantConsent,
  listConsents,
  revokeConsent,
  listPreferences,
  patchPreference,
  type NotificationPreference,
  type NotificationPreferenceChange,
} from "../../api/notifications";
import {
  createDeletion,
  createExport,
  downloadExport,
  listDeletions,
  listExports,
} from "../../api/support";
import { ConfirmDelete } from "../../components/ui/ConfirmDelete";

const CATEGORY_LABELS: Record<string, string> = {
  account: "Account",
  prescription: "Prescriptions",
  order: "Orders",
  payment: "Payments",
  support: "Support",
  // Prescription dose reminders and the Reminders page's (water, medicine, meal) alike.
  dose: "Reminders",
  marketing: "Offers & news",
};

const CONSENT_LABELS: Record<string, string> = {
  health_data_processing: "Health-data processing",
  data_sharing_pharmacy: "Share prescriptions with partner pharmacies",
  transactional_messaging: "Order, payment and reminder updates by SMS & WhatsApp",
  govt_id_storage: "Government-ID storage",
  terms_of_service: "Terms of service",
  privacy_policy: "Privacy policy",
};
// What the patient can switch off here (D-10); the government ID is withdrawn with a code (app).
const WITHDRAWABLE = ["data_sharing_pharmacy", "transactional_messaging"];

export function Settings() {
  return (
    <div className="max-w-2xl space-y-6">
      <h2 className="text-lg font-semibold">Settings</h2>
      <PreferencesPanel />
      <ConsentPanel />
      <PrivacyPanel />
    </div>
  );
}

function PrivacyPanel() {
  const qc = useQueryClient();
  const { data: exports } = useQuery({ queryKey: ["dpdp-exports"], queryFn: listExports });
  const { data: deletions } = useQuery({ queryKey: ["dpdp-deletions"], queryFn: listDeletions });
  const [reason, setReason] = useState("");
  // Asking for deletion is confirmed by typing "delete" first.
  const [confirmingDeletion, setConfirmingDeletion] = useState(false);

  const requestExport = useMutation({
    mutationFn: () => createExport("full"),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["dpdp-exports"] }),
  });
  const requestDeletion = useMutation({
    mutationFn: () => createDeletion(reason.trim() || "user requested"),
    onSuccess: () => { setReason(""); qc.invalidateQueries({ queryKey: ["dpdp-deletions"] }); },
    onSettled: () => setConfirmingDeletion(false),
  });
  // A request already in flight is shown instead of the form (ACCT-20): asking again only
  // repeated it.
  const openDeletion = (deletions ?? []).find((d) => d.status === "pending" || d.status === "approved");
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const isExpired = (at: string | null) => !!at && new Date(at).getTime() < Date.now();

  return (
    <section className="rounded-xl border bg-white shadow-xs">
      <div className="border-b px-5 py-3 text-sm font-medium">Your data (DPDP)</div>
      <div className="space-y-4 p-5">
        <div>
          <div className="flex items-center justify-between">
            <div className="text-sm">Export my data</div>
            <button onClick={() => requestExport.mutate()} disabled={requestExport.isPending} className="rounded-lg border px-3 py-1.5 text-sm disabled:opacity-50">
              Request export
            </button>
          </div>
          {downloadError && <p className="mt-2 text-sm text-red-600">{downloadError}</p>}
          {requestExport.isError && (
            <p className="mt-2 text-sm text-red-600">{describeError(requestExport.error)}</p>
          )}
          <ul className="mt-2 space-y-1 text-xs text-gray-500">
            {(exports ?? []).map((e) => (
              <li key={e.id} className="flex items-center justify-between">
                <span>{new Date(e.created_at).toLocaleString()} · {e.status}</span>
                {e.status === "ready" &&
                  (isExpired(e.signed_url_expires_at) ? (
                    <span>expired — request a new export</span>
                  ) : (
                    <button
                      onClick={() => {
                        setDownloadError(null);
                        downloadExport(e.id).catch((err) => setDownloadError(describeError(err)));
                      }}
                      className="text-brand hover:underline"
                    >
                      Download
                    </button>
                  ))}
              </li>
            ))}
          </ul>
        </div>

        <div className="border-t pt-4">
          <div className="text-sm">Delete my account</div>
          <p className="text-xs text-gray-400">An admin reviews deletion requests; data under legal hold can’t be deleted yet.</p>
          {openDeletion ? (
            <p className="mt-2 text-sm text-gray-600">
              You asked on {new Date(openDeletion.created_at).toLocaleDateString()}; the request is {openDeletion.status}.
            </p>
          ) : (
            <div className="mt-2 flex gap-2">
              <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason" className="min-w-0 flex-1 rounded-lg border px-3 py-1.5 text-sm" />
              <button
                onClick={() => setConfirmingDeletion(true)}
                disabled={requestDeletion.isPending}
                className="shrink-0 rounded-lg border px-3 py-1.5 text-sm text-red-600 disabled:opacity-50"
              >
                Request deletion
              </button>
            </div>
          )}
          {requestDeletion.isError && (
            <p className="mt-2 text-sm text-red-600">{describeError(requestDeletion.error)}</p>
          )}
          <ul className="mt-2 space-y-1 text-xs text-gray-500">
            {(deletions ?? []).map((d) => (
              <li key={d.id}>{new Date(d.created_at).toLocaleString()} · {d.status}{d.reject_reason ? ` (${d.reject_reason})` : ""}</li>
            ))}
          </ul>
          <ConfirmDelete
            open={confirmingDeletion}
            title="Delete your account?"
            confirmLabel="Request deletion"
            busyLabel="Requesting…"
            busy={requestDeletion.isPending}
            onConfirm={() => requestDeletion.mutate()}
            onClose={() => setConfirmingDeletion(false)}
          >
            <p>
              This asks for your account and its data to be deleted. An administrator reviews the request; once it is
              approved, the account can't be restored.
            </p>
          </ConfirmDelete>
        </div>
      </div>
    </section>
  );
}

function PreferencesPanel() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["notif-prefs"], queryFn: listPreferences });
  // Only the switched channel is sent (ACCT-16); the row updates at once, then from the server.
  const patch = useMutation({
    mutationFn: (change: NotificationPreferenceChange) => patchPreference(change),
    onMutate: (change) =>
      qc.setQueryData<NotificationPreference[]>(["notif-prefs"], (rows) =>
        rows?.map((r) => (r.category === change.category ? { ...r, ...change } : r)),
      ),
    onSettled: () => qc.invalidateQueries({ queryKey: ["notif-prefs"] }),
  });

  return (
    <section className="rounded-xl border bg-white shadow-xs">
      <div className="border-b px-5 py-3 text-sm font-medium">Notification preferences</div>
      {patch.isError && (
        <div className="border-b bg-red-50 px-5 py-2 text-sm text-red-700">{describeError(patch.error)}</div>
      )}
      <div className="divide-y">
        {/* The heading and every row share fixed tracks, so each heading sits over its switches:
            content-sized ("auto") tracks made the 48px headings and the narrower switches disagree,
            up to 30px (RESP-15). */}
        <div className="grid grid-cols-[1fr_3rem_3rem_3rem] items-center gap-3 px-5 py-2 text-xs text-gray-400">
          <span>Category</span><span className="text-center">Push</span><span className="text-center">SMS</span><span className="text-center">Inbox</span>
        </div>
        {(data ?? []).map((p) => (
          <div key={p.category} className="grid grid-cols-[1fr_3rem_3rem_3rem] items-center gap-3 px-5 py-3 text-sm">
            <span>{CATEGORY_LABELS[p.category] ?? p.category}</span>
            <Toggle label={`${CATEGORY_LABELS[p.category] ?? p.category}: push`} checked={p.push_enabled} onChange={(v) => patch.mutate({ category: p.category, push_enabled: v })} />
            <Toggle label={`${CATEGORY_LABELS[p.category] ?? p.category}: SMS`} checked={p.sms_enabled} onChange={(v) => patch.mutate({ category: p.category, sms_enabled: v })} />
            <Toggle label={`${CATEGORY_LABELS[p.category] ?? p.category}: inbox`} checked={p.inbox_enabled} onChange={(v) => patch.mutate({ category: p.category, inbox_enabled: v })} />
          </div>
        ))}
      </div>
    </section>
  );
}

function ConsentPanel() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["consents"], queryFn: listConsents });
  const change = useMutation({
    mutationFn: ({ type, on }: { type: string; on: boolean }) => (on ? grantConsent(type) : revokeConsent(type)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["consents"] }),
  });

  return (
    <section className="rounded-xl border bg-white shadow-xs">
      <div className="border-b px-5 py-3 text-sm font-medium">Consents</div>
      {change.isError && (
        <div className="border-b bg-red-50 px-5 py-2 text-sm text-red-700">{describeError(change.error)}</div>
      )}
      <ul className="divide-y">
        {(data ?? []).map((c) => {
          const required = c.required_version !== null;
          const switchable = WITHDRAWABLE.includes(c.consent_type);
          return (
            <li key={c.consent_type} className="flex items-center justify-between gap-3 px-5 py-3 text-sm">
              <div className="min-w-0">
                <div>{CONSENT_LABELS[c.consent_type] ?? c.consent_type}</div>
                <div className="text-xs text-gray-400">
                  {required
                    ? c.granted
                      ? `Accepted${c.current_version ? ` · ${c.current_version}` : ""}`
                      : "Not accepted yet"
                    : c.granted
                      ? c.consent_type === "govt_id_storage"
                        ? "On — withdraw it in the EMediExp app (it needs a code)"
                        : "On"
                      : "Off"}
                </div>
              </div>
              {c.needs_action ? (
                <button
                  onClick={() => change.mutate({ type: c.consent_type, on: true })}
                  disabled={change.isPending}
                  className="shrink-0 rounded-lg bg-brand px-3 py-1.5 text-xs font-medium text-brand-fg disabled:opacity-50"
                >
                  {c.current_version ? "Re-accept" : "Accept"}
                </button>
              ) : switchable ? (
                <div className="shrink-0">
                  <Toggle
                    label={CONSENT_LABELS[c.consent_type] ?? c.consent_type}
                    checked={c.granted}
                    onChange={(on) => {
                      if (!change.isPending) change.mutate({ type: c.consent_type, on });
                    }}
                  />
                </div>
              ) : !c.granted ? (
                <button
                  onClick={() => change.mutate({ type: c.consent_type, on: true })}
                  disabled={change.isPending}
                  className="shrink-0 rounded-lg border px-3 py-1.5 text-xs font-medium disabled:opacity-50"
                >
                  Grant
                </button>
              ) : (
                <span className="shrink-0 text-xs text-emerald-600">✓</span>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** A switch (RESP-16): announced as one with its on / off state and what it controls, 44×24, and
 *  tappable a little beyond its edges (44px tall, into the gaps beside it, never onto a neighbour). */
function Toggle({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={`relative mx-auto flex h-6 w-11 items-center rounded-full p-0.5 transition after:absolute after:-inset-x-1 after:-inset-y-2.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 focus-visible:ring-offset-2 ${checked ? "bg-brand" : "bg-gray-300"}`}
    >
      <span className={`h-5 w-5 rounded-full bg-white shadow-xs transition ${checked ? "translate-x-5" : ""}`} />
    </button>
  );
}
